/**
 * Symphenia Theme System
 *
 * Jedes Theme definiert CSS Custom Properties (HSL-Werte für shadcn/ui)
 * sowie Sidebar-Farben und Typografie-Tokens.
 *
 * Neue Themes einfach als weiteres Objekt in THEMES hinzufügen.
 */

export interface SympheniaTheme {
  id: string;
  name: string;          // Anzeigename (i18n-Key oder direkt)
  isDark: boolean;

  // ── shadcn/ui HSL-Variablen (ohne "hsl()") ──────────────────
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  radius: string;

  // ── Sidebar ─────────────────────────────────────────────────
  sidebarBg: string;         // Hintergrundfarbe Sidebar (hex oder hsl())
  sidebarFg: string;         // Text/Icons
  sidebarActiveBg: string;   // Aktiver Nav-Link Hintergrund
  sidebarActiveFg: string;   // Aktiver Nav-Link Text
  sidebarBorder: string;     // Trennlinien
  sidebarMuted: string;      // Gedämpfte Texte (E-Mail, Captions)

  // ── Typografie ───────────────────────────────────────────────
  fontSans: string;          // CSS font-family Stack — nur Namen, nichts wird von außen geladen
}

// ────────────────────────────────────────────────────────────────
// Vordefinierte Themes
// ────────────────────────────────────────────────────────────────

export const THEMES: Record<string, SympheniaTheme> = {

  /** Standard: Neutral Gray (Inverse of Dark Mode) · Steel Blue */
  symphenia: {
    id: 'symphenia',
    name: 'Symphenia',
    isDark: false,

    //  Light mode = mirror image of dark mode (Midnight):
    //  Dark:  #1a1a1a bg · #f0f0f0 text · #0ea5e9 accent
    //  Light: #f2f2f2 bg · #1a1a1a text · #0ea5e9 accent
    background:         '0 0% 95%',    // #f2f2f2 Neutral Light
    foreground:         '0 0% 10%',    // #1a1a1a Near-black
    card:               '0 0% 91%',    // #e8e8e8
    cardForeground:     '0 0% 10%',
    popover:            '0 0% 91%',
    popoverForeground:  '0 0% 10%',
    primary:            '199 89% 49%', // #0ea5e9 Steel Blue
    primaryForeground:  '0 0% 100%',
    secondary:          '0 0% 86%',    // #dcdcdc
    secondaryForeground:'0 0% 10%',
    muted:              '0 0% 86%',    // #dcdcdc
    mutedForeground:    '0 0% 42%',    // #6a6a6a Muted
    accent:             '199 89% 49%', // #0ea5e9 Steel Blue
    accentForeground:   '0 0% 100%',
    destructive:        '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border:             '0 0% 84%',    // slightly darker than surface
    input:              '0 0% 84%',
    ring:               '199 89% 49%',
    radius:             '0.75rem',

    sidebarBg:          '#e8e8e8',   // Neutral surface – harmonisch zum Content
    sidebarFg:          '#1a1a1a',   // Near-black – wie Styleguide
    sidebarActiveBg:    '#0ea5e9',   // Steel Blue – aktiver Nav-Eintrag (Pill)
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      'rgba(0,0,0,0.10)',
    sidebarMuted:       '#6a6a6a',   // Neutral Muted

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Symphenia Classic: Warm Cream · Braun RGB(46,23,0) · Steel Blue */
  'symphenia-classic': {
    id: 'symphenia-classic',
    name: 'Symphenia Classic',
    isDark: false,

    //  Basis: RGB(46,23,0) = #2e1700 = hsl(30, 100%, 9%)
    background:         '34 38% 89%',   // #f0e8d4 Warm Cream
    foreground:         '30 100% 9%',   // #2e1700 – exakt RGB(46,23,0)
    card:               '32 28% 84%',   // #e5dac6
    cardForeground:     '30 100% 9%',
    popover:            '32 28% 84%',
    popoverForeground:  '30 100% 9%',
    primary:            '199 89% 49%',  // #0ea5e9 Steel Blue
    primaryForeground:  '0 0% 100%',
    secondary:          '32 22% 81%',   // #dad0bc
    secondaryForeground:'30 100% 9%',
    muted:              '32 18% 85%',
    mutedForeground:    '30 45% 40%',   // #996633 Braun Muted
    accent:             '199 89% 49%',
    accentForeground:   '0 0% 100%',
    destructive:        '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border:             '30 25% 76%',
    input:              '30 25% 76%',
    ring:               '199 89% 49%',
    radius:             '0.75rem',

    sidebarBg:          '#e5dac6',   // Cream Surface – harmonisch zum Content
    sidebarFg:          '#2e1700',   // RGB(46,23,0) – exakt wie Styleguide
    sidebarActiveBg:    '#0ea5e9',   // Steel Blue – aktiver Nav-Eintrag (Pill)
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      'rgba(46,23,0,0.12)',
    sidebarMuted:       '#8c5c28',   // hsl(30,55%,36%)

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Slate Professional: Klares Neutralgrau · Blau */
  slate: {
    id: 'slate',
    name: 'Slate Professional',
    isDark: false,

    background:         '210 20% 98%',
    foreground:         '222 47% 11%',
    card:               '0 0% 100%',
    cardForeground:     '222 47% 11%',
    popover:            '0 0% 100%',
    popoverForeground:  '222 47% 11%',
    primary:            '221 83% 53%',  // Vivid Blue
    primaryForeground:  '0 0% 100%',
    secondary:          '210 40% 96%',
    secondaryForeground:'222 47% 11%',
    muted:              '210 40% 96%',
    mutedForeground:    '215 16% 47%',
    accent:             '221 83% 53%',
    accentForeground:   '0 0% 100%',
    destructive:        '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border:             '214 32% 91%',
    input:              '214 32% 91%',
    ring:               '221 83% 53%',
    radius:             '0.5rem',

    sidebarBg:          '#1e293b',   // slate-800
    sidebarFg:          '#94a3b8',   // slate-400
    sidebarActiveBg:    '#3b82f6',   // blue-500
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      '#334155',   // slate-700
    sidebarMuted:       '#64748b',   // slate-500

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Forest: Warmes Grün · Naturweiß */
  forest: {
    id: 'forest',
    name: 'Forest',
    isDark: false,

    background:         '120 15% 96%',
    foreground:         '140 30% 12%',
    card:               '120 10% 98%',
    cardForeground:     '140 30% 12%',
    popover:            '120 10% 98%',
    popoverForeground:  '140 30% 12%',
    primary:            '142 76% 36%',  // Emerald Green
    primaryForeground:  '0 0% 100%',
    secondary:          '120 12% 91%',
    secondaryForeground:'140 30% 12%',
    muted:              '120 10% 93%',
    mutedForeground:    '140 15% 40%',
    accent:             '142 76% 36%',
    accentForeground:   '0 0% 100%',
    destructive:        '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border:             '120 10% 87%',
    input:              '120 10% 87%',
    ring:               '142 76% 36%',
    radius:             '0.625rem',

    sidebarBg:          '#14532d',   // green-900
    sidebarFg:          '#86efac',   // green-300
    sidebarActiveBg:    '#16a34a',   // green-600
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      '#166534',   // green-800
    sidebarMuted:       '#4ade80',   // green-400

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Symphenia Nacht: Helle Symphenia-Sidebar · Dunkler Midnight-Inhaltsbereich */
  'symphenia-nacht': {
    id: 'symphenia-nacht',
    name: 'Symphenia Nacht',
    isDark: true,

    // ── Inhaltsbereich: identisch mit Midnight ───────────────────
    background:         '0 0% 10%',    // #1a1a1a
    foreground:         '0 0% 94%',    // #f0f0f0
    card:               '0 0% 15%',    // #262626
    cardForeground:     '0 0% 94%',
    popover:            '0 0% 15%',
    popoverForeground:  '0 0% 94%',
    primary:            '199 89% 49%', // #0ea5e9 Steel Blue
    primaryForeground:  '0 0% 100%',
    secondary:          '0 0% 20%',    // #333333
    secondaryForeground:'0 0% 94%',
    muted:              '0 0% 20%',    // #333333
    mutedForeground:    '0 0% 60%',    // #9a9a9a
    accent:             '199 89% 49%', // #0ea5e9 Steel Blue
    accentForeground:   '0 0% 100%',
    destructive:        '0 63% 44%',
    destructiveForeground: '0 0% 98%',
    border:             '0 0% 22%',
    input:              '0 0% 22%',
    ring:               '199 89% 49%',
    radius:             '0.75rem',

    // ── Sidebar: identisch mit Symphenia Standard ────────────────
    sidebarBg:          '#e8e8e8',   // Neutral Light – wie Symphenia Standard
    sidebarFg:          '#1a1a1a',   // Near-black
    sidebarActiveBg:    '#0ea5e9',   // Steel Blue Pill
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      'rgba(0,0,0,0.10)',
    sidebarMuted:       '#6a6a6a',

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Midnight: Dunkles Theme · Steel Blue Akzent (Styleguide: #1a1a1a Bg, #0ea5e9 Accent) */
  midnight: {
    id: 'midnight',
    name: 'Midnight',
    isDark: true,

    background:         '0 0% 10%',    // #1a1a1a – Styleguide dark bg
    foreground:         '0 0% 94%',    // #f0f0f0 – Styleguide dark text
    card:               '0 0% 15%',    // #262626 – Styleguide dark surface
    cardForeground:     '0 0% 94%',
    popover:            '0 0% 15%',
    popoverForeground:  '0 0% 94%',
    primary:            '199 89% 49%', // #0ea5e9 Steel Blue – Markenfarbe
    primaryForeground:  '0 0% 100%',
    secondary:          '0 0% 20%',    // #333333 – Styleguide surface-2
    secondaryForeground:'0 0% 94%',
    muted:              '0 0% 20%',    // #333333
    mutedForeground:    '0 0% 60%',    // #9a9a9a – Styleguide dark muted
    accent:             '199 89% 49%', // #0ea5e9 Steel Blue
    accentForeground:   '0 0% 100%',
    destructive:        '0 63% 44%',
    destructiveForeground: '0 0% 98%',
    border:             '0 0% 22%',    // leicht heller als surface-2
    input:              '0 0% 22%',
    ring:               '199 89% 49%', // Steel Blue
    radius:             '0.5rem',

    sidebarBg:          '#242424',   // leicht heller als der Arbeitsbereich (#1a1a1a) – grenzt den Menübalken ab
    sidebarFg:          '#9a9a9a',   // Styleguide dark muted
    sidebarActiveBg:    '#0ea5e9',   // Steel Blue – konsistent zur Marke
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      'rgba(255,255,255,0.10)',
    sidebarMuted:       '#666666',

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /** Steel Blue: Cream Content · Steel-Blue-Sidebar · Kakaobraun-Text */
  steelblue: {
    id: 'steelblue',
    name: 'Steel Blue',
    isDark: false,

    background:         '34 38% 89%',   // #f0e8d4 Warm Cream
    foreground:         '30 100% 9%',   // #2e1700
    card:               '32 28% 84%',   // #e5dac6
    cardForeground:     '30 100% 9%',
    popover:            '32 28% 84%',
    popoverForeground:  '30 100% 9%',
    primary:            '199 89% 49%',  // #0ea5e9 Steel Blue
    primaryForeground:  '0 0% 100%',
    secondary:          '32 22% 81%',   // #dad0bc
    secondaryForeground:'30 100% 9%',
    muted:              '32 18% 85%',
    mutedForeground:    '30 45% 40%',
    accent:             '199 89% 49%',
    accentForeground:   '0 0% 100%',
    destructive:        '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border:             '30 25% 76%',
    input:              '30 25% 76%',
    ring:               '199 89% 49%',
    radius:             '0.75rem',

    sidebarBg:          '#075985',   // Dark Steel Blue
    sidebarFg:          '#bae6fd',   // sky-200 – gut lesbar auf dunkelblau
    sidebarActiveBg:    'rgba(255,255,255,0.18)',  // Helles Frosted-Highlight
    sidebarActiveFg:    '#ffffff',
    sidebarBorder:      '#0c4a6e',   // sky-900
    sidebarMuted:       '#7dd3fc',   // sky-300

    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export const DEFAULT_THEME_ID = 'symphenia';

/** Wandelt ein Theme-Objekt in ein Flat-Record der CSS-Variablen um */
export function themeToCssVars(theme: SympheniaTheme): Record<string, string> {
  return {
    '--background':               theme.background,
    '--foreground':               theme.foreground,
    '--card':                     theme.card,
    '--card-foreground':          theme.cardForeground,
    '--popover':                  theme.popover,
    '--popover-foreground':       theme.popoverForeground,
    '--primary':                  theme.primary,
    '--primary-foreground':       theme.primaryForeground,
    '--secondary':                theme.secondary,
    '--secondary-foreground':     theme.secondaryForeground,
    '--muted':                    theme.muted,
    '--muted-foreground':         theme.mutedForeground,
    '--accent':                   theme.accent,
    '--accent-foreground':        theme.accentForeground,
    '--destructive':              theme.destructive,
    '--destructive-foreground':   theme.destructiveForeground,
    '--border':                   theme.border,
    '--input':                    theme.input,
    '--ring':                     theme.ring,
    '--radius':                   theme.radius,
    // Sidebar Tokens
    '--sidebar-bg':               theme.sidebarBg,
    '--sidebar-fg':               theme.sidebarFg,
    '--sidebar-active-bg':        theme.sidebarActiveBg,
    '--sidebar-active-fg':        theme.sidebarActiveFg,
    '--sidebar-border':           theme.sidebarBorder,
    '--sidebar-muted':            theme.sidebarMuted,
    // Typografie
    '--font-sans':                theme.fontSans,
  };
}
